import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { RefreshCw, ArrowLeft, Code2, Copy, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const WINDOWS = [7, 30, 90];
const SITE_LABELS = { amazon: "Amazon", ebay: "eBay", walmart: "Walmart", unknown: "Unknown" };

const fmtPct = (v) => (v == null ? "—" : `${v}%`);
const fmtNum = (v) => (v == null ? "0" : Number(v).toLocaleString());

// StatTile — a bare hero number with a label; no plot, so no hover layer needed.
// `emphasize` swaps the value to reflection-amber — reserved for a number that's normally
// zero and worth a second look when it isn't (e.g. Clarity Lock escape-hatch usage).
function StatTile({ label, value, sub, emphasize }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`mt-1 text-2xl font-semibold ${emphasize ? "text-reflection-foreground" : "text-foreground"}`}>{value}</div>
        {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function SectionTitle({ children, caption }) {
  return (
    <div className="mb-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground">{children}</h2>
      {caption && <p className="mt-0.5 text-xs text-muted-foreground">{caption}</p>}
    </div>
  );
}

// Two-category magnitude split. Direct-labeled, 2px surface gap between fills, rounded
// data-ends — identity is never color-alone. Reused for "resisted vs bought anyway" and
// for "kept both vs confirmed same" — same shape, different labels/colors/emptyText.
function SplitBar({ leftLabel, leftCount, leftColor, rightLabel, rightCount, rightColor, emptyText, ariaLabel }) {
  const total = leftCount + rightCount;
  if (total === 0) {
    return <p className="text-sm text-muted-foreground">{emptyText}</p>;
  }
  const leftPct = (leftCount / total) * 100;
  const rightPct = 100 - leftPct;
  return (
    <div>
      <div className="flex h-7 w-full gap-0.5 overflow-hidden rounded-md" role="img" aria-label={ariaLabel}>
        {leftCount > 0 && (
          <div className="h-full rounded-md" style={{ width: `${leftPct}%`, backgroundColor: leftColor }} title={`${leftLabel}: ${leftCount}`} />
        )}
        {rightCount > 0 && (
          <div className="h-full rounded-md" style={{ width: `${rightPct}%`, backgroundColor: rightColor }} title={`${rightLabel}: ${rightCount}`} />
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: leftColor }} />
          {leftLabel} <span className="font-medium text-foreground">{fmtNum(leftCount)}</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: rightColor }} />
          {rightLabel} <span className="font-medium text-foreground">{fmtNum(rightCount)}</span>
        </span>
      </div>
    </div>
  );
}

function ResistBar({ resisted, bought }) {
  return (
    <SplitBar
      leftLabel="Resisted" leftCount={resisted} leftColor="#8FB996"
      rightLabel="Bought anyway" rightCount={bought} rightColor="#eda426"
      emptyText="No warnings shown in this window yet."
      ariaLabel={`${resisted} resisted, ${bought} bought anyway`}
    />
  );
}

// Q6 at-a-glance: is DOM scraping producing purchases on this retailer?
function captureStatus(row) {
  if (row.purchases > 0) return { color: "#8FB996", label: "Capturing" };
  if (row.reached_checkout > 0) return { color: "#eda426", label: "No captures" };
  return { color: "#9ca3af", label: "No data" };
}

function StatusDot({ row }) {
  const s = captureStatus(row);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
      <span className="text-xs text-muted-foreground">{s.label}</span>
    </span>
  );
}

// Reference SQL behind each card, for pasting into the Supabase SQL editor to
// double-check a number. Text comes from the backend (colocated with the
// aggregation logic it mirrors) — this just renders + copies it.
function SqlPanel({ queries }) {
  const [copiedId, setCopiedId] = useState(null);

  const handleCopy = async (id, sql) => {
    try {
      await navigator.clipboard.writeText(sql);
      setCopiedId(id);
      toast.success("SQL copied");
      setTimeout(() => setCopiedId((prev) => (prev === id ? null : prev)), 1500);
    } catch {
      toast.error("Couldn't copy — select the text manually");
    }
  };

  if (!queries?.length) return null;

  return (
    <section className="mb-8">
      <SectionTitle caption="Run any of these in the Supabase SQL editor to double-check a number on this page.">
        SQL reference
      </SectionTitle>
      <div className="space-y-3">
        {queries.map((q) => (
          <Card key={q.id}>
            <CardContent className="p-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{q.title}</div>
                <Button variant="outline" size="sm" onClick={() => handleCopy(q.id, q.sql)}>
                  {copiedId === q.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  <span className="ml-1.5">{copiedId === q.id ? "Copied" : "Copy"}</span>
                </Button>
              </div>
              <pre className="overflow-x-auto rounded-md border border-border bg-gray-50 p-3 text-xs leading-relaxed text-foreground dark:bg-gray-900">
                <code>{q.sql}</code>
              </pre>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

export default function Admin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [showSql, setShowSql] = useState(false);

  const load = (days) => {
    setLoading(true);
    api.getAdminMetrics(days)
      .then((res) => { setData(res.data); setError(null); })
      .catch((err) => setError(err.response?.status === 403 ? "forbidden" : "error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(windowDays); }, [windowDays]);

  if (loading && !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-navy" />
      </div>
    );
  }

  if (error === "forbidden") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-6 text-center">
        <h1 className="text-lg font-semibold text-foreground">Not authorized</h1>
        <p className="text-sm text-muted-foreground">This page is limited to CartControl admins.</p>
        <Link to="/dashboard" className="text-sm font-medium text-navy hover:underline">Back to dashboard</Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-6 text-center">
        <p className="text-sm text-muted-foreground">Couldn't load metrics.</p>
        <Button onClick={() => load(windowDays)} variant="outline" size="sm">Retry</Button>
      </div>
    );
  }

  const { users, funnel, duplicates, capture_by_site } = data;
  const generatedAt = data.generated_at ? new Date(data.generated_at).toLocaleString() : "";

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to="/dashboard" className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3 w-3" /> Dashboard
            </Link>
            <h1 className="text-xl font-semibold text-foreground">Analytics</h1>
            {generatedAt && <p className="text-xs text-muted-foreground">Updated {generatedAt}</p>}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-border p-0.5">
              {WINDOWS.map((d) => (
                <button
                  key={d}
                  onClick={() => setWindowDays(d)}
                  className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                    windowDays === d ? "bg-navy text-white" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <Button onClick={() => setShowSql((v) => !v)} variant={showSql ? "default" : "outline"} size="sm">
              <Code2 className="h-3.5 w-3.5" />
              <span className="ml-1.5">{showSql ? "Hide SQL" : "View SQL"}</span>
            </Button>
            <Button onClick={() => load(windowDays)} variant="outline" size="sm" disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {showSql && <SqlPanel queries={data.queries} />}

        {/* Users (all-time) */}
        <section className="mb-8">
          <SectionTitle caption="All-time totals across every user.">Users</SectionTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <StatTile
              label="Total users"
              value={fmtNum(users.total)}
              sub={`${fmtNum(users.free)} free · ${fmtNum(users.active_trials)} trial · ${fmtNum(users.premium)} premium`}
            />
            <StatTile label="New · 7 days" value={fmtNum(users.new_7d)} />
            <StatTile label="New · 30 days" value={fmtNum(users.new_30d)} />
            <StatTile label="Gmail connected" value={fmtNum(users.gmail_connected)} sub={`${fmtPct(users.gmail_connected_pct)} of users`} />
            <StatTile label="Trial → paid" value={fmtPct(users.trial_to_paid_pct)} sub={`${fmtNum(users.premium)} paid · ${fmtNum(users.expired_no_convert)} expired`} />
          </div>
        </section>

        {/* Behavioral funnel (window) */}
        <section className="mb-8">
          <SectionTitle caption={`Extension events in the last ${data.window_days} days.`}>Behavioral funnel</SectionTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="Reached checkout" value={fmtNum(funnel.reached_checkout)} />
            <StatTile label="Warnings shown" value={fmtNum(funnel.intervention_shown)} />
            <StatTile label="Purchases recorded" value={fmtNum(funnel.purchase_recorded)} />
            <StatTile label="Resist rate" value={fmtPct(funnel.resist_rate_pct)} sub="of warned carts abandoned" />
          </div>
          <Card className="mt-3">
            <CardContent className="p-4">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                After a warning
              </div>
              <ResistBar resisted={funnel.cart_abandoned} bought={funnel.purchase_after_warning} />
            </CardContent>
          </Card>
        </section>

        {/* Passive duplicate-flag resolution — how users answer "is this the same purchase?",
            and whether the Clarity Lock escape hatch (confirm is logged, not blocked) is being
            used at an unusual rate. */}
        <section className="mb-8">
          <SectionTitle caption={`How users answered passive duplicate flags in the last ${data.window_days} days.`}>
            Duplicate resolution
          </SectionTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="Flags resolved" value={fmtNum(duplicates.resolved_total)} />
            <StatTile label="Confirm rate" value={fmtPct(duplicates.confirm_rate_pct)} sub="marked as the same purchase" />
            <StatTile label="Still open" value={fmtNum(duplicates.still_suspected)} sub="awaiting an answer, all-time" />
            <StatTile
              label="Confirmed under Clarity Lock"
              value={fmtNum(duplicates.confirmed_under_clarity_lock)}
              sub="not blocked — logged for review"
              emphasize={duplicates.confirmed_under_clarity_lock > 0}
            />
          </div>
          <Card className="mt-3">
            <CardContent className="p-4">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Resolution split
              </div>
              <SplitBar
                leftLabel="Kept both" leftCount={duplicates.dismissed} leftColor="#8FB996"
                rightLabel="Confirmed same" rightCount={duplicates.confirmed} rightColor="#eda426"
                emptyText="No duplicate flags resolved in this window yet."
                ariaLabel={`${duplicates.dismissed} kept both, ${duplicates.confirmed} confirmed same`}
              />
            </CardContent>
          </Card>
        </section>

        {/* Per-retailer DOM-scrape health (Q6) */}
        <section>
          <SectionTitle caption={`DOM capture per retailer, last ${data.window_days} days. Amazon has no client-side pixel, so its captures are always heuristic.`}>
            Capture health
          </SectionTitle>
          <Card>
            <CardContent className="p-0">
              {capture_by_site.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">No purchase or checkout events in this window yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Retailer</TableHead>
                        <TableHead className="text-right">Purchases</TableHead>
                        <TableHead className="text-right">Pixel</TableHead>
                        <TableHead className="text-right">Heuristic</TableHead>
                        <TableHead className="text-right">Checkouts</TableHead>
                        <TableHead className="text-right">Capture rate</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {capture_by_site.map((row) => (
                        <TableRow key={row.site}>
                          <TableCell className="font-medium text-foreground">{SITE_LABELS[row.site] || row.site}</TableCell>
                          <TableCell className="text-right">{fmtNum(row.purchases)}</TableCell>
                          <TableCell className="text-right">{fmtNum(row.pixel)}</TableCell>
                          <TableCell className="text-right">{fmtNum(row.heuristic)}</TableCell>
                          <TableCell className="text-right">{fmtNum(row.reached_checkout)}</TableCell>
                          <TableCell className="text-right">{fmtPct(row.capture_rate_pct)}</TableCell>
                          <TableCell><StatusDot row={row} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
